import { ConfigSection } from './GeneralConfig'
import { useEffect, useMemo, type FC } from 'react'
import { getUseConfigStore } from '../../stores/useConfigStore'
import { isPaywalled } from '~/features/billing/utils/plan'
import { nanoid } from 'nanoid'
import { Button, Input, Label, Textarea, Tooltip, TooltipContent, TooltipTrigger } from '@bpinternal/ui-kit-next'
import { ImageInput } from '../elements/ImageInput'
import { useFeature } from '~/hooks'
import { Plan } from '~/features/billing/types'
import { useQuery } from '~/services'

type Props = {
  workspaceId: string
  botId: string
  plan: Plan
  title?: string
  description?: string
}

export const AboutSection: FC<Props> = ({
  workspaceId,
  botId,
  plan,
  title = 'About your bot',
  description = "Configure your bot's name, description, and avatar.",
}) => {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const botName = useConfigStore((state) => state.botName)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const botDescription = useConfigStore((state) => state.botDescription)
  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const footer = useConfigStore((state) => state.footer)
  const fabImage = useConfigStore((state) => state.fabImage)

  const setBotName = useConfigStore((state) => state.setBotName)
  const setBotAvatar = useConfigStore((state) => state.setBotAvatar)
  const setBotDescription = useConfigStore((state) => state.setBotDescription)
  const setComposerPlaceholder = useConfigStore((state) => state.setComposerPlaceholder)
  const setFooter = useConfigStore((state) => state.setFooter)
  const setFabImage = useConfigStore((state) => state.setFabImage)

  const injectToggleEmbedFeatureEnabled = useFeature('webchat-inject-toggle-embed')
  const minPlan = 'plus'
  const isFeaturePaywalled = isPaywalled(plan, minPlan)

  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    workspaceId,
    botId,
  })

  const saveConfig = useConfigStore((state) => state.saveConfig)

  useEffect(() => {
    if (!botName || botName === '') {
      setBotName(bot?.name ?? 'Bot')
      saveConfig().catch(console.error)
    }
  }, [bot?.name, botName, saveConfig, setBotName])

  return (
    <ConfigSection title={title} description={description}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <ImageInput
            workspaceId={workspaceId}
            botId={botId}
            title="Bot Avatar"
            imgKey={`avatar-${nanoid(6)}`}
            imgUrl={botAvatar ?? ''}
            setImgUrl={setBotAvatar}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-muted-foreground">Bot name</Label>
            <Input value={botName} onChange={(e) => setBotName(e.target.value)} placeholder="My chatbot" />
          </div>
        </div>
        <div>
          <Label className="text-muted-foreground">Description</Label>
          <Textarea
            value={botDescription}
            onChange={(e) => setBotDescription(e.target.value)}
            placeholder="A brief description of your chatbot"
            className="w-full"
          />
        </div>
        <div>
          <Label className="text-muted-foreground">Composer placeholder</Label>
          <Input
            value={composerPlaceholder}
            onChange={(e) => setComposerPlaceholder(e.target.value)}
            placeholder="Type your message..."
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-muted-foreground">Footer</Label>
          {isFeaturePaywalled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Input disabled={true} placeholder="Footer content" value="⚡ By Botpress" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Only available on Plus and higher plans</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <div>
              <Input placeholder="Footer content" value={footer || ''} onChange={(e) => setFooter(e.target.value)} />
            </div>
          )}
        </div>
        {!injectToggleEmbedFeatureEnabled && (
          <div className="flex items-center gap-3">
            <ImageInput
              workspaceId={workspaceId}
              botId={botId}
              title="FAB Image"
              imgKey={`fab-${nanoid(6)}`}
              imgUrl={fabImage ?? ''}
              setImgUrl={setFabImage}
            />
            <div className="flex flex-col">
              <Label className="text-muted-foreground">Floating button image</Label>
              <Button
                variant="outline"
                color={'gray'}
                size={'sm'}
                onClick={() => setFabImage(botAvatar)}
                className=" mt-1 h-7 font-normal text-gray-12"
              >
                Use bot avatar
              </Button>
            </div>
          </div>
        )}
      </div>
    </ConfigSection>
  )
}
